/**
 * 汉字拼音首字母提取工具
 *
 * 基于 pinyin-pro 库，支持所有常用汉字和多音字。
 *
 * 用法：
 *   getPinyinInitials('张三') → 'zs'
 *   getPinyinInitials('赵四') → 'zs'
 *   getPinyinInitials('李明') → 'lm'
 *   matchPinyinInitials('张三', 'zs') → true
 *   matchPinyinInitials('张三', 'z') → true（前缀匹配）
 */

import { pinyin } from 'pinyin-pro';

/**
 * 获取字符串的拼音首字母序列
 *
 * @param str 输入字符串（如 "张三"）
 * @returns 拼音首字母序列（如 "zs"）
 *
 * 示例：
 *   getPinyinInitials('张三') → 'zs'
 *   getPinyinInitials('李明') → 'lm'
 *   getPinyinInitials('ABC123') → 'abc'
 *   getPinyinInitials('张三abc') → 'zsabc'
 */
export function getPinyinInitials(str: string): string {
  if (!str) return '';
  return str
    .split('')
    .map(char => {
      const code = char.charCodeAt(0);
      // ASCII 字母直接返回小写
      if (code >= 65 && code <= 90) return char.toLowerCase();
      if (code >= 97 && code <= 122) return char;
      // 非汉字字符返回空
      if (code < 0x4E00 || code > 0x9FFF) return '';
      // 汉字：用 pinyin-pro 获取首字母
      const py = pinyin(char, { pattern: 'first', toneType: 'none' });
      return py || '';
    })
    .filter(c => c !== '')
    .join('');
}

/**
 * 判断搜索词是否匹配目标字符串（支持拼音首字母匹配）
 *
 * 匹配规则：
 * 1. 直接包含匹配：目标字符串包含搜索词（如 "张三" 包含 "张"）
 * 2. 拼音首字母包含匹配：目标的拼音首字母序列包含搜索词（如 "zs" 匹配 "张三"）
 *
 * @param target 目标字符串（如学员姓名）
 * @param query 搜索词（如 "zs"、"z"、"张"）
 * @returns 是否匹配
 */
export function matchPinyinInitials(target: string, query: string): boolean {
  if (!query) return true;
  if (!target) return false;

  const lowerQuery = query.toLowerCase();
  const lowerTarget = target.toLowerCase();

  // 1. 直接包含匹配
  if (lowerTarget.includes(lowerQuery)) return true;

  // 2. 拼音首字母匹配
  const initials = getPinyinInitials(target).toLowerCase();
  if (initials.includes(lowerQuery)) return true;

  return false;
}
